import React, { useEffect, useMemo, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { get, throttle } from "lodash";
import { PvHeader } from "./pv-header";
import { MobileHeader } from "./mobile-header";
import StickyBanner from "../../molecules/sticky-banner";
import StickyNavbar from "../../molecules/sticky-navbar";
import { setMakeSecondaryApiCallsTriggered, setUserDetails, setIsLogin } from "../../store/actions";
import { addTokensToLocalStorage, getUserDetails } from "../../helper/utils";
import GoogleOneTapSignInUp from "../../atoms/google-one-tap";
import { GoogleOAuthProvider, googleLogout } from "@react-oauth/google";
import { OPEN_HAMBURGER_MENU, OPEN_SEARCHBAR } from "../../store/actions";
import EPaperBanner from "../../atoms/epaper-banner";
// import ElectionFloatingWidget from "../../atoms/election-floating-widget";
import { pushPageDataLayer, addSignUpLoginToDataLayer, addLogoutToDataLayer, addAuthTrackingToDataLayer } from "../../helper/datalayer-utils";

const Header = () => {
  const dispatch = useDispatch();
  const userdetails = useSelector((state) => get(state, ["user", "user"], {}));
  const pageType = useSelector((state) => get(state, ["qt", "pageType"]));
  const pageData = useSelector((state) => get(state, ["qt", "data"], []));
  const isHamburgerMenuOpen = useSelector((state) => get(state, ["isHamburgerMenuOpen"], false));
  const bottomNavBarMenus = useSelector((state) =>
    get(state, ["qt", "data", "navigationMenu", "bottomNavBar"], [])
  );

  const makeSecondaryApiCallsDelay = 2750;
  const [makeSecondaryApiCalls, setMakeSecondaryApiCalls] = useState(false);
  const [isMobile, setMobile] = useState(true);
  const [loadOneTap, setGoogleOneTap] = useState(false);
  const GoogleClientId = useSelector((state) =>
    get(state, ["qt", "config", "publisher-attributes", "google_client_id"], "")
  );
  const ssoEndpoint = useSelector((state) =>
    get(state, ["qt", "config", "publisher-attributes", "sso_endpoint"], null)
  );

  const dataLayerPushedRef = useRef(null);
  const trackingTimerRef = useRef(null);
  const trackingDataRef = useRef({ pageType, userdetails, pageData });

  useEffect(() => {
    trackingDataRef.current = { pageType, userdetails, pageData };
  }, [pageType, userdetails, pageData]);

  useEffect(() => {
    if (typeof window === "undefined" || !pageType) return;
    const contentId = pageData?.story?.id || pageData?.section?.id || pageData?.tag?.id || "";
    const userId = userdetails?.userData?.id || userdetails?.id || "anonymous";
    const currentRouteId = `${pageType}-${contentId}-${userId}`;

    if (dataLayerPushedRef.current !== currentRouteId) {
      dataLayerPushedRef.current = currentRouteId;
      if (trackingTimerRef.current) clearTimeout(trackingTimerRef.current);

      trackingTimerRef.current = setTimeout(() => {
        const { pageType: pt, userdetails: ud, pageData: pd } = trackingDataRef.current;
        pushPageDataLayer(pt, ud, pd);
      }, 150);
    }
  }, [pageType, pageData, userdetails]);

  const toggleHandler = () => {
    dispatch({
      type: OPEN_HAMBURGER_MENU,
      isHamburgerMenuOpen: !isHamburgerMenuOpen
    });

    dispatch({
      type: OPEN_SEARCHBAR,
      isSearchBarOpen: false
    });
  };

  const getScroll = () => {
    if (typeof window !== "undefined") {
      if (window.scrollY > 700) {
        !makeSecondaryApiCalls && setMakeSecondaryApiCalls(true);
      }
    }
  };

  useEffect(() => {
    if (window && window.innerWidth < 1025) {
      setMobile(true);
    } else {
      setMobile(false);
      setMakeSecondaryApiCalls(true);
    }
    if (window) {
      const searchQuery = window.location.search;
      const accessTokenIndex = searchQuery.indexOf("?access_token");
      const output = accessTokenIndex !== -1 ? searchQuery.substring(accessTokenIndex) : searchQuery;
      const searchParams = new URLSearchParams(output);
      const access_token = searchParams.get("access_token");
      const refresh_token = searchParams.get("refresh_token");
      const userLocalStorage = localStorage.getItem("user");
      const stored_access_token = localStorage.getItem("access_token");
      const stored_refresh_token = localStorage.getItem("refresh_token");

      if (stored_refresh_token && stored_access_token) {
        dispatch(setIsLogin(true));
      }
      if (access_token && refresh_token) {
        addTokensToLocalStorage(access_token, refresh_token);
        getUserDetails()
          .then((data) => {
            if (data?.status === 401) {
              // Remove an tokens from localStorage
              localStorage.removeItem("access_token");
              localStorage.removeItem("refresh_token");
              localStorage.removeItem("user");
              localStorage.removeItem("commentsToken");

              googleLogout();
              // loggingout user if refresh token expires
              window.location.href = `${ssoEndpoint}/auth/logout?redirectUrl=${window.location.href.split("?")[0]}`;
            } else {
              addSignUpLoginToDataLayer(window.location.href, data);
              sessionStorage.setItem("skip_auth_tracking", "true");
              dispatch(setIsLogin(true));
              dispatch(setUserDetails(data || {}));
              window.localStorage.setItem("user", JSON.stringify(data));

              const searchParam = accessTokenIndex !== -1 ? searchQuery.substring(0, accessTokenIndex) : searchQuery;
              window.history.replaceState({}, "", window.location.pathname + searchParam);
            }
          })
          .catch((err) => {
            console.log(err);
          });
      } else if (userLocalStorage && userLocalStorage !== "undefined" && userdetails?.userData === undefined) {
        if (typeof userLocalStorage === "string" && userLocalStorage.trim() !== "") {
          const parsedUser = JSON.parse(userLocalStorage);
          dispatch(setUserDetails(parsedUser));
        }
      } else {
        setGoogleOneTap(true);
      }
    }
  }, []);

  useEffect(() => {
    window.addEventListener("scroll", throttle(getScroll, 250));
    return () => window.removeEventListener("scroll", getScroll);
  }, []);

  useEffect(() => {
    makeSecondaryApiCalls &&
      dispatch(setMakeSecondaryApiCallsTriggered(makeSecondaryApiCalls, makeSecondaryApiCallsDelay, isMobile));
  }, [makeSecondaryApiCalls]);

  const isHardcodedHeader = useMemo(() => {
    let headerType = "";
    if (pageType === "section-page" || pageType === "sub-section-page") {
      const customHeader = pageData?.collection?.metadata?.["custom-header"];
      if (customHeader === "cok2025") {
        headerType = "cok2025";
      } else if (customHeader === "festivals") {
        headerType = "festivals";
      } else if (customHeader === "Canara-Robeco") {
        headerType = "Canara-Robeco";
      }
    }
    return headerType;
  }, [pageType, pageData]);

  return (
    <GoogleOAuthProvider clientId={GoogleClientId}>
      {loadOneTap && <GoogleOneTapSignInUp />}
      <div className="header-hide-tab">
        <PvHeader isHardcodedHeader={isHardcodedHeader} />
      </div>
      <div className="header-hide-dektop">
        <MobileHeader isHardcodedHeader={isHardcodedHeader} />
      </div>

      <EPaperBanner utmMedium="topbanner" pageType={pageType} isHardcodedHeader={isHardcodedHeader} />
      {isHardcodedHeader !== "Canara-Robeco" && <StickyBanner />}
      <StickyNavbar onMenuToggle={toggleHandler} menuItems={bottomNavBarMenus} />
      {/* <ElectionFloatingWidget /> */}
    </GoogleOAuthProvider>
  );
};

export { Header };
